import { AnyAction } from 'redux';
import { FormikErrors } from 'formik';
import { HandledError } from '@synergy/api';
import lodashSet from 'lodash/set';

import {
    SUPPLIER_SPECIAL_OFFERS_DETAIL_OVERVIEW_ERROR,
    SUPPLIER_SPECIAL_OFFERS_DETAIL_OVERVIEW_RECEIVE,
    SUPPLIER_SPECIAL_OFFERS_DETAIL_OVERVIEW_UPDATE,
} from './supplier-special-offers-detail-overview-actions';
import { SUPPLIER_SPECIAL_OFFERS_DETAIL_RESET } from '../../store/supplier-special-offers-detail-actions';
import { SupplierSpecialOffer } from '../../../models/supplier-special-offer';
import { SupplierSpecialOffersOverviewFormValues } from '../models/supplier-special-offers-overview-form-values';

export interface SupplierSpecialOffersDetailOverviewReducerState {
    supplierSpecialOffer?: Partial<SupplierSpecialOffer>;
    isValid: boolean;
    isDirty: boolean;
    overviewErrors: FormikErrors<SupplierSpecialOffersOverviewFormValues>;
    /**
     * The offer as the server last handed it over (#2159). The copy above
     * cannot serve: switching tabs parks its input right there.
     */
    baseline?: Partial<SupplierSpecialOffer>;
}

const initialSupplierSpecialOffersDetailsOverviewReducerState: SupplierSpecialOffersDetailOverviewReducerState = {
    isValid: true,
    isDirty: false,
    overviewErrors: {},
};

export const supplierSpecialOffersDetailOverviewReducer = (
    state: SupplierSpecialOffersDetailOverviewReducerState = initialSupplierSpecialOffersDetailsOverviewReducerState,
    action: AnyAction
): SupplierSpecialOffersDetailOverviewReducerState => {
    switch (action.type) {
        case SUPPLIER_SPECIAL_OFFERS_DETAIL_RESET: {
            return initialSupplierSpecialOffersDetailsOverviewReducerState;
        }

        case SUPPLIER_SPECIAL_OFFERS_DETAIL_OVERVIEW_RECEIVE: {
            return {
                ...state,
                supplierSpecialOffer: action['supplierSpecialOffer'],
                // The same values, kept apart from the ones the mask edits.
                baseline: action['supplierSpecialOffer'],
            };
        }

        case SUPPLIER_SPECIAL_OFFERS_DETAIL_OVERVIEW_UPDATE: {
            const { formData, isDirty, isValid, errors, locale } = action;

            const nextSupplierSpecialOffer: Partial<SupplierSpecialOffer> = {
                ...state.supplierSpecialOffer,
                ...formData,
                title: { ...state.supplierSpecialOffer?.title, [locale]: formData.title },
                subtitle: { ...state.supplierSpecialOffer?.subtitle, [locale]: formData.subtitle },
            };

            return {
                ...state,
                supplierSpecialOffer: nextSupplierSpecialOffer,
                isDirty: state.isDirty || isDirty,
                isValid,
                overviewErrors: errors,
            };
        }

        case SUPPLIER_SPECIAL_OFFERS_DETAIL_OVERVIEW_ERROR: {
            const formErrors: FormikErrors<SupplierSpecialOffersOverviewFormValues> = { ...state.overviewErrors };

            (action['errors'] as HandledError<string>[])?.forEach(error => {
                lodashSet(formErrors, error.property, error.error);
            });

            return {
                ...state,
                isValid: false,
                overviewErrors: formErrors,
            };
        }

        default: {
            return state;
        }
    }
};
